import { addDays, addWeeks, addMonths } from "date-fns";
import { Users } from "lucide-react";
import { useMemo } from "react";
import { DateRange } from "react-day-picker";
import { Line, LineChart, XAxis, YAxis, CartesianGrid } from "recharts";
import {
    getDateKeyForGrouping,
    getWeekStartKey,
    getMonthKeyForGrouping,
    getGroupingGranularity,
    formatDateForDisplay,
    formatWeekRange,
} from "./utils";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
    ChartContainer,
    ChartTooltip,
    ChartTooltipContent,
} from "@/components/ui/chart";
import { CreditActionCode } from "@/types/creditAction";
import { ICreditTransaction } from "@/types/creditTransaction";

interface UsersAnalyticsProps {
    transactionsData?: ICreditTransaction[];
    dateRange: DateRange;
}

export const UsersAnalytics = ({
    transactionsData,
    dateRange,
}: UsersAnalyticsProps) => {
    const from = useMemo(() => dateRange?.from ?? new Date(), [dateRange]);
    const to = useMemo(() => dateRange?.to ?? new Date(), [dateRange]);
    const granularity = getGroupingGranularity(from, to);

    const signUpTrendsData = useMemo(() => {
        if (!transactionsData) return [];

        // Filter transactions by SIGN_UP actionCode
        const signUpTransactions = transactionsData.filter(
            (transaction: ICreditTransaction) =>
                transaction.actionCode === CreditActionCode.SIGN_UP,
        );

        // Group by granularity
        const groupKeyFn =
            granularity === "month"
                ? getMonthKeyForGrouping
                : granularity === "week"
                  ? getWeekStartKey
                  : getDateKeyForGrouping;

        // Count sign-ups per group
        const groupedSignUps = signUpTransactions.reduce(
            (acc: Record<string, number>, transaction: ICreditTransaction) => {
                const key = groupKeyFn(transaction.createdAt);
                acc[key] = (acc[key] || 0) + 1;
                return acc;
            },
            {},
        );

        // Generate complete date range
        const generateDateRange = () => {
            const dates: string[] = [];
            let currentDate = new Date(from);
            const endDate = new Date(to);

            while (currentDate <= endDate) {
                let key: string;

                if (granularity === "month") {
                    key = getMonthKeyForGrouping(currentDate);
                    currentDate = addMonths(currentDate, 1);
                } else if (granularity === "week") {
                    key = getWeekStartKey(currentDate);
                    currentDate = addWeeks(currentDate, 1);
                } else {
                    key = getDateKeyForGrouping(currentDate);
                    currentDate = addDays(currentDate, 1);
                }

                if (!dates.includes(key)) {
                    dates.push(key);
                }
            }

            return dates;
        };

        const completeDateRange = generateDateRange();

        // Build chart data with all dates in range
        return completeDateRange
            .map(key => ({
                key: String(key),
                signUps: groupedSignUps[key] || 0,
            }))
            .sort((a, b) => String(a.key).localeCompare(String(b.key)));
    }, [transactionsData, granularity, from, to]);

    const chartConfig = {
        signUps: {
            label: "Sign-ups",
            theme: {
                light: "hsl(var(--admin-chart-1))",
                dark: "hsl(var(--admin-chart-1))",
            },
        },
    };

    const totalSignUps = signUpTrendsData.reduce(
        (sum, item) => sum + item.signUps,
        0,
    );

    return (
        <Card className="h-full">
            <CardHeader>
                <CardTitle className="flex items-center gap-2">
                    <Users className="h-5 w-5" />
                    Users Analytics
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
                <div className="rounded-lg bg-admin-muted p-4">
                    <div className="mb-2 flex items-center justify-between">
                        <div className="flex flex-col">
                            <h4 className="text-sm font-semibold">
                                Sign-up Trends Analysis
                            </h4>
                        </div>
                        <div className="mt-4 flex items-center gap-4 pr-4 text-xs text-admin-muted-foreground">
                            <span className="font-semibold">
                                Total Sign-ups:
                            </span>{" "}
                            {totalSignUps}
                        </div>
                    </div>

                    {signUpTrendsData.length > 0 ? (
                        <ChartContainer
                            config={chartConfig}
                            className="h-64 w-full"
                        >
                            <LineChart
                                data={signUpTrendsData}
                                {...{ overflow: "visible" }}
                            >
                                <CartesianGrid vertical={false} />
                                <XAxis
                                    tickMargin={10}
                                    angle={granularity === "day" ? -45 : 0}
                                    dataKey="key"
                                    tick={{ fontSize: 10 }}
                                    tickFormatter={value => {
                                        if (granularity === "month") {
                                            const [year, month] =
                                                String(value).split("-");
                                            return `${new Date(Number(year), Number(month) - 1).toLocaleString("en-US", { month: "short", year: "2-digit" })}`;
                                        } else if (granularity === "week") {
                                            return formatWeekRange(
                                                String(value),
                                            );
                                        } else {
                                            return formatDateForDisplay(
                                                value,
                                                "MM/d",
                                            );
                                        }
                                    }}
                                />
                                <YAxis tick={{ fontSize: 12 }} />
                                <ChartTooltip
                                    content={<ChartTooltipContent />}
                                />
                                <Line
                                    type="monotone"
                                    dataKey="signUps"
                                    stroke="hsl(var(--admin-chart-1))"
                                    strokeWidth={2}
                                    dot={{
                                        fill: "hsl(var(--admin-chart-1))",
                                        strokeWidth: 2,
                                        r: 4,
                                    }}
                                    activeDot={{
                                        r: 6,
                                        stroke: "hsl(var(--admin-chart-1))",
                                        strokeWidth: 2,
                                    }}
                                />
                            </LineChart>
                        </ChartContainer>
                    ) : (
                        <div className="flex h-64 items-center justify-center text-admin-muted-foreground">
                            No sign-up data available for the selected period
                        </div>
                    )}
                </div>
            </CardContent>
        </Card>
    );
};
